import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Shield,
  Users,
  CreditCard,
  Mail,
  Gift,
  Lock,
  HardDrive,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronRight,
  Search,
  FileText,
  MessageSquare,
  Ticket,
  Globe,
  Bell,
  UserX,
  Download
} from 'lucide-react';

const SiteRulesManager = () => {
  const [expandedSections, setExpandedSections] = useState({
    admin: true,
    user: true,
    billing: true,
    email: true,
    referral: true,
    security: true
  });
  const [searchQuery, setSearchQuery] = useState('');

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const rules = {
    admin: {
      title: "Admin Rules",
      icon: Shield,
      color: "text-red-400",
      bgColor: "bg-red-500/10",
      borderColor: "border-red-500/30",
      rules: [
        {
          id: "admin-001",
          rule: "Protected Admin Account",
          description: "mel@mypatriotmail.org cannot be deleted by anyone, ever",
          status: "enforced",
          location: "auth_routes.py → delete_account"
        },
        {
          id: "admin-002",
          rule: "Admin Self-Delete Prevention",
          description: "Admin accounts cannot delete themselves through the UI",
          status: "enforced",
          location: "auth_routes.py → delete_account"
        },
        {
          id: "admin-003",
          rule: "Admin Impersonation",
          description: "Admins can impersonate any user to debug issues",
          status: "enforced",
          location: "admin_routes.py → impersonate"
        },
        {
          id: "admin-004",
          rule: "Danger Zone Hidden for Admins",
          description: "Delete Account section is hidden for admin accounts in UI",
          status: "enforced",
          location: "AccountSettingsPage.jsx"
        },
        {
          id: "admin-005",
          rule: "Super Admin Access",
          description: "Only admins can access /admin dashboard and related APIs",
          status: "enforced",
          location: "AdminPage.jsx, admin_routes.py"
        }
      ]
    },
    user: {
      title: "User Rules",
      icon: Users,
      color: "text-blue-400",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/30",
      rules: [
        {
          id: "user-001",
          rule: "Closed Network Email",
          description: "Users can only email other @mypatriotmail.org addresses",
          status: "enforced",
          location: "email_routes.py → send_email"
        },
        {
          id: "user-002",
          rule: "Username Format",
          description: "Usernames: alphanumeric, dots, underscores, hyphens only (2-50 chars)",
          status: "enforced",
          location: "auth_routes.py → register"
        },
        {
          id: "user-003",
          rule: "Password Requirements",
          description: "Minimum 8 characters required for passwords",
          status: "enforced",
          location: "auth_routes.py → register, change_password"
        },
        {
          id: "user-004",
          rule: "Account Deletion",
          description: "Users must type 'DELETE MY ACCOUNT' + password to delete",
          status: "enforced",
          location: "auth_routes.py → delete_account"
        },
        {
          id: "user-005",
          rule: "Data Export Available",
          description: "Users can export all their data (CSV/JSON) at any time",
          status: "enforced",
          location: "export_routes.py"
        },
        {
          id: "user-006",
          rule: "Email Signature",
          description: "Users can create custom email signatures with referral links",
          status: "enforced",
          location: "AccountSettingsPage.jsx, auth_routes.py"
        }
      ]
    },
    billing: {
      title: "Billing & Subscription Rules",
      icon: CreditCard,
      color: "text-green-400",
      bgColor: "bg-green-500/10",
      borderColor: "border-green-500/30",
      rules: [
        {
          id: "bill-001",
          rule: "Free Trial Period",
          description: "New users get a configurable trial period (default: 7 days)",
          status: "enforced",
          location: "auth_routes.py → register"
        },
        {
          id: "bill-002",
          rule: "Stripe Integration",
          description: "All payments processed through Stripe",
          status: "enforced",
          location: "payment_routes.py, webhook_routes.py"
        },
        {
          id: "bill-003",
          rule: "Coupon System",
          description: "Discount coupons can be applied at checkout (percentage or fixed)",
          status: "enforced",
          location: "coupon_routes.py, payment_routes.py"
        },
        {
          id: "bill-004",
          rule: "Dual Code Registration",
          description: "Users can enter both referral code AND discount coupon at signup",
          status: "enforced",
          location: "RegisterPage.jsx"
        },
        {
          id: "bill-005",
          rule: "Plan-Based Limits",
          description: "Storage limits and features vary by subscription plan",
          status: "enforced",
          location: "plans collection, attachment_routes.py"
        }
      ]
    },
    email: {
      title: "Email Rules",
      icon: Mail,
      color: "text-purple-400",
      bgColor: "bg-purple-500/10",
      borderColor: "border-purple-500/30",
      rules: [
        {
          id: "email-001",
          rule: "Internal Network Only",
          description: "Emails can only be sent within @mypatriotmail.org domain",
          status: "enforced",
          location: "email_routes.py"
        },
        {
          id: "email-002",
          rule: "Encryption Option",
          description: "Users can choose to encrypt emails (toggle in compose)",
          status: "enforced",
          location: "ComposeModal.jsx, email_routes.py"
        },
        {
          id: "email-003",
          rule: "Attachment Size Limits",
          description: "File uploads limited by user's storage quota",
          status: "enforced",
          location: "attachment_routes.py, email_routes.py"
        },
        {
          id: "email-004",
          rule: "Auto-Save Drafts",
          description: "Compose modal can save drafts before sending",
          status: "enforced",
          location: "ComposeModal.jsx"
        },
        {
          id: "email-005",
          rule: "Labels & Organization",
          description: "Users can create custom labels to organize emails",
          status: "enforced",
          location: "label_routes.py, EmailPage.jsx"
        },
        {
          id: "email-006",
          rule: "Auto-Empty Trash on Logout",
          description: "All emails in trash are permanently deleted when user logs out",
          status: "enforced",
          location: "email_routes.py → empty_trash, AuthContext.jsx → logout"
        }
      ]
    },
    referral: {
      title: "Referral Program Rules",
      icon: Gift,
      color: "text-amber-400",
      bgColor: "bg-amber-500/10",
      borderColor: "border-amber-500/30",
      rules: [
        {
          id: "ref-001",
          rule: "Referral Code Generation",
          description: "Each user gets a unique 8-character referral code",
          status: "enforced",
          location: "auth_routes.py → register"
        },
        {
          id: "ref-002",
          rule: "10 Referrals = 1 Free Year",
          description: "Users with 10 paid referrals get 1 year free subscription",
          status: "enforced",
          location: "invite_routes.py → check_and_grant_rewards"
        },
        {
          id: "ref-003",
          rule: "50 Referrals = Lifetime",
          description: "Users with 50 paid referrals get lifetime subscription",
          status: "enforced",
          location: "invite_routes.py → check_and_grant_rewards"
        },
        {
          id: "ref-004",
          rule: "30-Day Cookie Tracking",
          description: "Referral codes tracked via 30-day browser cookie",
          status: "enforced",
          location: "RegisterPage.jsx, LandingPage.jsx"
        },
        {
          id: "ref-005",
          rule: "Conversion Tracking",
          description: "Referrals only count when referred user completes payment",
          status: "enforced",
          location: "webhook_routes.py → stripe_webhook"
        }
      ]
    },
    security: {
      title: "Security Rules",
      icon: Lock,
      color: "text-cyan-400",
      bgColor: "bg-cyan-500/10",
      borderColor: "border-cyan-500/30",
      rules: [
        {
          id: "sec-001",
          rule: "JWT Authentication",
          description: "All API requests require valid JWT token (except public routes)",
          status: "enforced",
          location: "auth.py → get_current_user"
        },
        {
          id: "sec-002",
          rule: "Password Hashing",
          description: "Passwords hashed with bcrypt before storage",
          status: "enforced",
          location: "auth.py → get_password_hash"
        },
        {
          id: "sec-003",
          rule: "CORS Configuration",
          description: "CORS enabled for frontend domain only",
          status: "enforced",
          location: "server.py"
        },
        {
          id: "sec-004",
          rule: "Contact Form Captcha",
          description: "Public contact form requires math captcha",
          status: "enforced",
          location: "ContactFormModal.jsx"
        },
        {
          id: "sec-005",
          rule: "Sensitive Data Exclusion",
          description: "Password hashes and _id never returned in API responses",
          status: "enforced",
          location: "All routes, MongoDB projections"
        },
        {
          id: "sec-006",
          rule: "S3-Compatible Storage",
          description: "Attachments stored in iDrive E2 (S3-compatible)",
          status: "enforced",
          location: "attachment_routes.py"
        }
      ]
    }
  };

  // Filter rules based on search
  const filterRules = (sectionRules) => {
    if (!searchQuery.trim()) return sectionRules;
    const query = searchQuery.toLowerCase();
    return sectionRules.filter(r => 
      r.rule.toLowerCase().includes(query) || 
      r.description.toLowerCase().includes(query) ||
      r.id.toLowerCase().includes(query)
    );
  };

  const totalRules = Object.values(rules).reduce((acc, section) => acc + section.rules.length, 0);

  return (
    <Card className="bg-[#1a1a2e] border-[#2d2d44]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#6D4AFF]" />
              Site Rules & Policies
            </CardTitle>
            <CardDescription className="text-gray-400">
              Platform rules and configurations ({totalRules} rules across {Object.keys(rules).length} categories)
            </CardDescription>
          </div>
          <Badge className="bg-[#6D4AFF]/20 text-[#6D4AFF] border-[#6D4AFF]/30">
            {totalRules} Rules
          </Badge>
        </div>
        
        {/* Search */}
        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Search rules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#252540] border-[#2d2d44] text-white"
          />
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {Object.entries(rules).map(([key, section]) => {
          const filteredRules = filterRules(section.rules);
          if (searchQuery && filteredRules.length === 0) return null;
          
          const Icon = section.icon;
          
          return (
            <div 
              key={key} 
              className={`border ${section.borderColor} rounded-xl overflow-hidden`}
            >
              {/* Section Header */}
              <button
                onClick={() => toggleSection(key)}
                className={`w-full p-4 ${section.bgColor} flex items-center justify-between hover:opacity-90 transition-opacity`}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${section.color}`} />
                  <span className="font-semibold text-white">{section.title}</span>
                  <Badge variant="outline" className={`${section.color} border-current`}>
                    {filteredRules.length} {filteredRules.length === 1 ? 'rule' : 'rules'}
                  </Badge>
                </div>
                {expandedSections[key] ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </button>
              
              {/* Section Content */}
              {expandedSections[key] && (
                <div className="p-4 space-y-3 bg-[#0d0d1a]/50">
                  {filteredRules.map((rule, index) => (
                    <div
                      key={rule.id}
                      className="p-4 bg-[#1a1a2e] border border-[#2d2d44] rounded-lg hover:border-[#3d3d54] transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs text-gray-500 font-mono">{rule.id}</span>
                            <Badge 
                              className={
                                rule.status === 'enforced' 
                                  ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                                  : rule.status === 'planned'
                                  ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                                  : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                              }
                            >
                              {rule.status === 'enforced' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                              {rule.status === 'planned' && <Clock className="w-3 h-3 mr-1" />}
                              {rule.status}
                            </Badge>
                          </div>
                          <h4 className="text-white font-medium">{rule.rule}</h4>
                          <p className="text-gray-400 text-sm mt-1">{rule.description}</p>
                          <p className="text-xs text-gray-600 mt-2 font-mono">
                            📍 {rule.location}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        
        {/* Legend */}
        <div className="mt-6 p-4 bg-[#252540] rounded-lg">
          <p className="text-sm text-gray-400 mb-2 font-medium">Status Legend:</p>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                enforced
              </Badge>
              <span className="text-gray-500">Currently active</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                <Clock className="w-3 h-3 mr-1" />
                planned
              </Badge>
              <span className="text-gray-500">Coming soon</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SiteRulesManager;
